import tracker from '@capturi/analytics'
import { PermissionRow, applyPermissionsRules } from '@capturi/core'
import { useFeatureFlags } from '@capturi/feature-flags'
import { useToast } from '@capturi/ui-components'
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Icon,
  Switch,
  Text,
  Tooltip,
  VStack,
  chakra,
} from '@chakra-ui/react'
import { Trans, t } from '@lingui/macro'
import React, { ReactElement, useEffect, useState } from 'react'
import { MdBolt } from 'react-icons/md'
import {
  PatchUser,
  defaultPermissionsPerRole,
  defaultUserPermissions,
  toUiRole,
} from '../../hooks/useUserActions'
import { Role, UserPermissions } from '../../types'
import { getCrudPermissions } from './PermissionsCrud'
import { getOtherPermissions } from './PermissionsOther'
import { getQAPermissions } from './PermissionsQA'

type Props = {
  userUid: string
  userName: string
  onClose: () => void
  userPermissions: Partial<UserPermissions>
  role: Role
  isOpen: boolean
  patchUser: PatchUser
}

const PermissionDrawer: React.FC<Props> = ({
  userUid,
  onClose,
  isOpen,
  userPermissions,
  patchUser,
  role,
  userName,
}) => {
  const [permissions, setPermissions] = useState<UserPermissions>({
    ...defaultUserPermissions,
    ...userPermissions,
  })
  const uiRole = toUiRole(role, permissions)

  const {
    enableMasterTracker,
    enableText,
    isArchiveOrg,
    enableQualityAssurance,
    enableScoring,
  } = useFeatureFlags()

  useEffect(() => {
    setPermissions({ ...defaultUserPermissions, ...userPermissions })
  }, [userPermissions])

  const toast = useToast()
  const [inProgress, setInProgress] = useState<boolean>(false)
  const defaultRolePermissions = defaultPermissionsPerRole[uiRole]

  const crudPermissions = getCrudPermissions({
    defaultRolePermissions,
    enableText,
    permissions,
    enableMasterTracker,
    isArchiveOrg,
  })

  const otherPermissions = getOtherPermissions({
    defaultRolePermissions,
    permissions,
    enableMasterTracker,
    enableText,
    isArchiveOrg,
  })

  const qaPermissions = getQAPermissions({
    defaultRolePermissions,
    permissions,
    enableText,
    enableQualityAssurance,
    isArchiveOrg,
  })
  const hideQualityAssurance =
    (!enableQualityAssurance && !enableScoring) || qaPermissions.length === 0

  const handleChange = async (
    key: keyof UserPermissions,
    value: boolean,
    userPermissions: UserPermissions,
  ): Promise<void> => {
    try {
      setInProgress(true)
      tracker.event('organizationPage_permissionChanged', {
        permission: key,
        value: value,
      })

      await patchUser(userUid, {
        permissions: userPermissions,
      })
      toast({
        title: t`Wuhuu, user updated`,
        status: 'success',
      })
    } catch (error) {
      let errMsg: null | string = null
      if (error instanceof Error) {
        errMsg = error.message
      }

      toast({
        title: t`Could not update the user's permission`,
        description: errMsg,
        status: 'error',
      })
    } finally {
      setInProgress(false)
    }
  }

  const togglePermission = (
    key: keyof UserPermissions,
    value: boolean,
  ): void => {
    const userPermissions = applyPermissionsRules(permissions, key, value)
    setPermissions(userPermissions)
    handleChange(key, value, userPermissions)
  }

  const renderRow = ({
    key,
    name,
    description,
    disabled = undefined,
    tooltip,
    tooltipStonlyTag = undefined,
    forcedIsChecked = undefined,
  }: PermissionRow): ReactElement => {
    const outerTooltipLabel =
      disabled && tooltip ? tooltip : t`This role can't have this permission`

    return (
      <HStack key={key} align="start">
        <Box mb={2}>
          <Tooltip
            isDisabled={!disabled}
            hasArrow
            label={outerTooltipLabel}
            placement="left"
          >
            <chakra.span ml={1}>
              <Switch
                size="sm"
                isDisabled={inProgress || disabled}
                onChange={(e) => togglePermission(key, e.currentTarget.checked)}
                isChecked={forcedIsChecked ?? permissions[key]}
              />
            </chakra.span>
          </Tooltip>
        </Box>
        <Box mb={2}>
          <Flex alignItems="center" gap={1}>
            <Text lineHeight="1.2">{name}</Text>
            {tooltip && disabled && (
              <Tooltip label={tooltip} placement="top" hasArrow>
                <Box display="inline-flex" alignItems="center" ml={2}>
                  <Icon
                    as={MdBolt}
                    borderRadius="base"
                    boxSize="18px"
                    background="primary.light"
                    color="primary.dark"
                    data-stonly={tooltipStonlyTag}
                  />
                </Box>
              </Tooltip>
            )}
          </Flex>
          <Text fontSize="xs" color="gray.600" lineHeight="1.5">
            {description}
          </Text>
        </Box>
      </HStack>
    )
  }

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader
          borderBottom="1px"
          borderBottomColor="greys.100"
          lineHeight={1.4}
          display="flex"
          alignItems="center"
          pr={10}
          mb={4}
        >
          <Trans>Permissions for {userName}</Trans>
        </DrawerHeader>
        <DrawerBody px={4}>
          <Box p={2}>
            <VStack align="start" gap={4}>
              {!isArchiveOrg && (
                <Box>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>
                    <Trans>Create, Edit and Delete</Trans>
                  </Text>
                  {crudPermissions.map(renderRow)}
                </Box>
              )}
              {!hideQualityAssurance && (
                <Box>
                  <Text fontSize="sm" fontWeight="bold" mb={2}>
                    <Trans>Quality Assurance</Trans>
                  </Text>
                  {qaPermissions.map(renderRow)}
                </Box>
              )}
              <Box>
                <Text fontSize="sm" fontWeight="bold" mb={2}>
                  <Trans>Other</Trans>
                </Text>

                {otherPermissions.map(renderRow)}
              </Box>
            </VStack>
          </Box>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  )
}

export default PermissionDrawer
